import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import type { AuthUser } from "../types/authUser";

export type CertificateOrganisation = { name?: string; logoUrl?: string };

const certificateOrganisationQuery = (soid: string | undefined) =>
  queryOptions({
    queryKey: ["certificate-organisation", soid],
    queryFn: () => api.certificates.getOrganisation(soid!),
    staleTime: 30 * 60 * 1000,
  });

// An Organisation admin's login already has the name and logo. A team
// member's login carries the team member's own name instead, so theirs are
// fetched; if that fails, certificates start blank rather than with their name.
const isTeamMember = (user: AuthUser | null | undefined) => user?.userType === "team";

const fromLogin = (user: AuthUser | null | undefined): CertificateOrganisation => ({
  name: user?.schoolName,
  logoUrl: user?.schoolLogo || undefined,
});

/** The Organisation's name and logo that certificates start from. */
export function useCertificateOrganisation(): {
  organisation: CertificateOrganisation;
  isLoading: boolean;
} {
  const { user } = useAuth();
  const teamMember = isTeamMember(user);
  const query = useQuery({
    ...certificateOrganisationQuery(user?.soid),
    enabled: teamMember && !!user?.soid,
  });

  if (!teamMember) return { organisation: fromLogin(user), isLoading: false };
  return { organisation: query.data ?? {}, isLoading: !!user?.soid && query.isLoading };
}

/** Waits for the Organisation's name and logo, for code that draws a certificate straight away. */
export function useLoadCertificateOrganisation(): () => Promise<CertificateOrganisation> {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return async () => {
    if (!isTeamMember(user)) return fromLogin(user);
    if (!user?.soid) return {};
    return queryClient.ensureQueryData(certificateOrganisationQuery(user.soid)).catch((error) => {
      console.error("Error loading the Organisation for certificates:", error);
      return {};
    });
  };
}
